'use client'

import { useState } from 'react'

import { formatDateOnly, formatDateYYYYMMDD } from '@/utils/date'
import { downloadBlob } from '@/utils/misc'
import {
  isExistInArrayStorage,
  storeArrayOfString,
} from '@/utils/storage-helpers'
import { withString } from '@/utils/string'
import { trackClickDownloadDataInduk } from '@/utils/tracker/modules/datainduk'
import { TrackerSection, TrackerSubSection } from '@/utils/tracker/shared'
import {
  DataIndukFiltersWithKeywordParams,
  useMutateDownloadDataInduk,
} from '@/apis/useDataInduk'

import { useBotContext } from '@/components/BotDetector/BotProvider'
import { CommonDownloadFileButton } from '@/components/CommonDownloadFileButton/CommonDownloadFileButton'
import { useDownloadSurvey } from '@/components/DownloadSurvey/DownloadSurveyProvider'

interface ButtonDownloadProps extends DataIndukFiltersWithKeywordParams {
  disabled?: boolean
  lastUpdatedAt: string
  extensions?: ('xlsx' | 'csv' | 'pdf')[]
  level?: 'Kecamatan' | 'Kabupaten' | 'Provinsi'
}

function generateFileTitle({
  bentuk,
  jalur,
  status,
  jenis,
  keyword,
  kodeKecamatan,
  kodeKabupaten,
  sortBy = 'bentuk_pendidikan',
  sortDir = 'asc',
  lastUpdatedAt,
  level,
}: ButtonDownloadProps) {
  const prefix =
    level === 'Kabupaten'
      ? `Data Induk Satuan Pendidikan Kabupaten Kota ${withString(kodeKabupaten)}`
      : `Data Induk Satuan Pendidikan ${level} ${withString(kodeKecamatan)}`

  return `${prefix}${withString(bentuk)}${withString(jalur)}${withString(status)}${withString(jenis)}${withString(sortBy)}${withString(sortDir).toUpperCase()}${withString(keyword)} - Per ${formatDateOnly(lastUpdatedAt)}`
}

const LATEST_DOWNLOADED_STORAGE_KEY = '__ldf_di_satpen'
const MAX_STORAGE_ITEM = 10

export const ButtonDownloadSatpen = ({
  disabled = false,
  bentuk,
  jalur,
  status,
  jenis,
  keyword,
  kodeKecamatan,
  kodeKabupaten,
  sortBy = 'bentuk_pendidikan',
  sortDir = 'asc',
  lastUpdatedAt,
  extensions = ['xlsx', 'csv', 'pdf'],
  level = 'Kecamatan',
}: ButtonDownloadProps) => {
  const { showSurvey } = useDownloadSurvey()

  const [selectedExt, setSelectedExt] = useState<'csv' | 'xlsx' | 'pdf'>('xlsx')
  const [isShowConfirmationDialog, setShowConfirmationDialog] =
    useState<boolean>(false)
  const [isShowErrorDialog, setShowErrorDialog] = useState<boolean>(false)

  const { isBot } = useBotContext()
  const { mutate, isPending } = useMutateDownloadDataInduk()

  const storageKeyForFile = `${withString(kodeKecamatan)}${withString(kodeKabupaten)}${withString(bentuk, '_')}${withString(jalur, '_')}${withString(status, '_')}${withString(jenis, '_')}${withString(sortBy, '_')}${withString(sortDir, '_')}${withString(keyword, '_')}_${formatDateYYYYMMDD(lastUpdatedAt).replace(/\s/g, '')}`

  const filename = generateFileTitle({
    bentuk,
    jalur,
    status,
    jenis,
    keyword,
    kodeKecamatan,
    kodeKabupaten,
    sortBy,
    sortDir,
    lastUpdatedAt,
    level,
  })

  const executeMutation = async (
    fileType: 'csv' | 'xlsx' | 'pdf',
    filenameExt: string
  ) => {
    if (!disabled) {
      trackClickDownloadDataInduk({
        bentuk,
        jalur,
        status,
        jenis,
        keyword,
        locationId: kodeKecamatan || kodeKabupaten || '',
        locationLevel: level,
        format: fileType,
        section: TrackerSection.SATPEN,
        subSection: TrackerSubSection.PAUD_DIKDASMEN,
      })
      mutate(
        {
          bentuk,
          jalur,
          status,
          jenis,
          keyword,
          kodeKecamatan,
          kodeKabupaten,
          sortBy,
          format: fileType,
        },
        {
          onSuccess(data) {
            downloadBlob(data, filenameExt)

            storeArrayOfString({
              key: LATEST_DOWNLOADED_STORAGE_KEY,
              max: MAX_STORAGE_ITEM,
              newItem: `${storageKeyForFile}_${fileType}`,
            })

            // Delay 1s before showing survey dialog
            setTimeout(() => {
              showSurvey('DATA_INDUK')
            }, 500)
          },
          onError() {
            setShowErrorDialog(true)
          },
        }
      )
    }
  }

  const handleDownload = async (
    fileType: 'csv' | 'xlsx' | 'pdf',
    skipStorageCheck = false
  ) => {
    const isExistInStorage = isExistInArrayStorage({
      key: LATEST_DOWNLOADED_STORAGE_KEY,
      itemToCheck: `${storageKeyForFile}_${fileType}`,
    })

    if (!isBot && !skipStorageCheck && isExistInStorage) {
      // show confirmation dialog
      setShowConfirmationDialog(true)
      setSelectedExt(fileType)
    } else {
      const fileWithExt = `${filename}.${fileType}`
      executeMutation(fileType, fileWithExt)
    }
  }

  const confirmDownload = () => {
    const fileWithExt = `${filename}.${selectedExt}`

    setShowConfirmationDialog(false)
    executeMutation(selectedExt, fileWithExt)
  }

  return (
    <>
      <CommonDownloadFileButton
        extensions={extensions}
        disabled={disabled}
        isPending={isPending}
        isShowErrorDialog={isShowErrorDialog}
        onCloseErrorDialog={() => {
          setShowErrorDialog(false)
        }}
        onDownload={(fileType) => {
          handleDownload(fileType, false)
        }}
        withRedownloadConfirmation={true}
        isShowConfirmationDialog={isShowConfirmationDialog}
        onCloseConfirmationDialog={() => {
          setShowConfirmationDialog(false)
        }}
        onConfirmed={confirmDownload}
      />
    </>
  )
}
